import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { computed, ComputedRef } from 'vue'
// stores
import { useUserSettingsStore } from '@/stores/UserSettingsStore'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)

export function useDate(): {
  timezone: ComputedRef<string>
  locale: ComputedRef<string>
  now: () => Dayjs
  parse: (dt: string) => Dayjs | null
  fromUtc: (dt: string) => Dayjs | null
  toUtc: (dt: string) => Dayjs | null
  format: (dt: string, fmt?: string) => string
  toISOString: (dt: string) => string
  toReadableTime: (ts: string, fmt?: string) => string
  toReadableDateTime: (ts: string, fmt?: string) => string
  toTimezone: (ts: string, tz?: string | null) => Dayjs
  timeFrom: (from: string, to?: Dayjs) => string
  formatTimestampToParts: (ts: string, length?: number, ago?: boolean) => string
  formatSecondsToParts: (secs: number, length?: number, ago?: boolean) => string
  readableDateRange: (startDate: string, endDate: string) => string
} {
  const userSettingsStore = useUserSettingsStore()

  const timezone = computed(() => userSettingsStore.userSettings?.timezone || dayjs.tz.guess())
  const locale = computed(() => userSettingsStore.userSettings?.locale || 'en-GB')

  const now = (): Dayjs => dayjs().tz(timezone.value).locale(locale.value)

  const parse = (dt: string): Dayjs | null => (dt ? dayjs(dt).tz(timezone.value).locale(locale.value) : null)

  const fromUtc = (dt: string): Dayjs | null => (dt ? dayjs.utc(dt).tz(timezone.value).locale(locale.value) : null)

  const toUtc = (dt: string): Dayjs | null => (dt ? dayjs(dt).tz(timezone.value).utc() : null)

  const format = (dt: string, fmt = 'YYYY-MM-DD HH:mm'): string => {
    const d = parse(dt)
    return d ? d.format(fmt) : ''
  }

  const toISOString = (dt: string): string => {
    const d = toUtc(dt)
    return d ? d.toISOString() : ''
  }

  const toReadableTime = (ts: string, fmt = 'HH:mm'): string => {
    const d = parse(ts)
    return d ? d.format(fmt) : ''
  }

  const toReadableDateTime = (ts: string, fmt = 'L'): string => {
    const d = parse(ts)
    return d ? d.format(fmt) : ''
  }

  const toTimezone = (ts: string, tz: string | null = null): Dayjs => {
    if (tz) return dayjs(ts).tz(tz)
    const localTz = localStorage.getItem('TZ')
    return localTz ? dayjs(ts).tz(localTz) : dayjs(ts)
  }

  const timeFrom = (from: string, to = dayjs()): string => dayjs(from).from(to)

  const formatTimestampToParts = (ts: string, length = 2, ago = false): string => {
    const elapsed = dayjs.duration(dayjs().diff(ts))
    const parts = [
      { value: elapsed.years(), label: 'y' },
      { value: elapsed.months(), label: 'mo' },
      { value: elapsed.days(), label: 'd' },
      { value: elapsed.hours(), label: 'h' },
      { value: elapsed.minutes(), label: 'm' },
      { value: elapsed.seconds(), label: 's' }
    ].filter(p => p.value > 0)

    const output = parts
      .slice(0, length)
      .map(p => `${p.value}${p.label}`)
      .join(' ')
    return ago ? `${output} ago` : output
  }

  const formatSecondsToParts = (secs: number, length = 2, ago = false): string => {
    const elapsed = dayjs.duration(secs, 'seconds')
    const parts = [
      { value: elapsed.years(), label: 'y' },
      { value: elapsed.months(), label: 'mo' },
      { value: elapsed.days(), label: 'd' },
      { value: elapsed.hours(), label: 'h' },
      { value: elapsed.minutes(), label: 'm' },
      { value: elapsed.seconds(), label: 's' }
    ].filter(p => p.value > 0)

    const output = parts
      .slice(0, length)
      .map(p => `${p.value}${p.label}`)
      .join(' ')
    return ago ? `${output} ago` : output
  }

  const readableDateRange = (startDate: string, endDate: string): string => {
    // Check if the input is just a date (no time specified)
    const isDateOnly = (dateStr: string): boolean => {
      return typeof dateStr === 'string' && !dateStr.includes(':') && !dateStr.includes('T')
    }

    let start = dayjs(startDate)
    let end = dayjs(endDate)

    if (!start.isValid() || !end.isValid()) return ''

    // If only date is provided, set appropriate times
    if (isDateOnly(startDate)) {
      start = start.startOf('day') // Set to 00:00:00
    }
    if (isDateOnly(endDate)) {
      end = end.endOf('day') // Set to 23:59:59
    }

    const today = dayjs().startOf('day')
    const yesterday = today.subtract(1, 'day')
    const lastWeek = today.subtract(1, 'week')
    const lastMonth = today.subtract(1, 'month')
    const lastYear = today.subtract(1, 'year')
    const tomorrow = today.add(1, 'day')
    const nextWeekStart = today.add(1, 'week').startOf('week')
    const nextWeekEnd = today.add(1, 'week').endOf('week')
    const nextMonth = today.add(1, 'month')
    const nextYear = today.add(1, 'year')

    const sameYear = start.year() === end.year()
    const isAllDay =
      (start.hour() === 0 &&
        start.minute() === 0 &&
        start.second() === 0 &&
        end.hour() === 23 &&
        end.minute() === 59 &&
        end.second() === 59) ||
      (start.hour() === 0 &&
        start.minute() === 0 &&
        start.second() === 0 &&
        end.hour() === 0 &&
        end.minute() === 0 &&
        end.second() === 0 &&
        start.format('YYYY-MM-DD') !== end.format('YYYY-MM-DD'))

    // Check for special date ranges
    if (start.isSame(end, 'day')) {
      if (start.isSame(today, 'day')) return 'Today'
      if (start.isSame(yesterday, 'day')) return 'Yesterday'
      if (start.isSame(tomorrow, 'day')) return 'Tomorrow'
    }

    if (isAllDay) {
      if (start.isSame(today, 'week') && end.isSame(today, 'week')) return 'This Week'
      if (start.isSame(lastWeek, 'week') && end.isSame(lastWeek, 'week')) return 'Last Week'
      if (start.isSame(nextWeekStart, 'week') && end.isSame(nextWeekEnd, 'week')) return 'Next Week'
      if (start.isSame(today, 'month') && end.isSame(today, 'month')) return 'This Month'
      if (start.isSame(lastMonth, 'month') && end.isSame(lastMonth, 'month')) return 'Last Month'
      if (start.isSame(nextMonth, 'month') && end.isSame(nextMonth, 'month')) return 'Next Month'
      if (start.isSame(today, 'year') && end.isSame(today, 'year')) return 'This Year'
      if (start.isSame(lastYear, 'year') && end.isSame(lastYear, 'year')) return 'Last Year'
      if (start.isSame(nextYear, 'year') && end.isSame(nextYear, 'year')) return 'Next Year'
    }

    // Default formatting for other date ranges
    if (sameYear) {
      return isAllDay
        ? `${start.format('D MMM')}-${end.format('D MMM')}`
        : `${start.format('D MMM, h:mma')}-${end.format('D MMM, h:mma')}`
    } else {
      return isAllDay
        ? `${start.format('D MMM YY')}-${end.format('D MMM YY')}`
        : `${start.format('D MMM YY, h:mma')}-${end.format('D MMM YY, h:mma')}`
    }
  }

  return {
    timezone,
    locale,
    now,
    parse,
    fromUtc,
    toUtc,
    format,
    toISOString,
    toReadableTime,
    toReadableDateTime,
    toTimezone,
    timeFrom,
    formatTimestampToParts,
    formatSecondsToParts,
    readableDateRange
  }
}
